import type { TagDefinition } from '@/lib/projectDataEntry'
import type { TagEntry } from '@/lib/tagEntry'

export interface PickerBinding {
  source: string
  /** Sent as the child's `group`; null heads the chain. */
  parentKey: string | null
  /** Null unless the payload names the key a picked value is emitted under. */
  idKey: string | null
}

export interface PickEmission {
  nameKey: string
  idKey: string
  idValue: string
}

export function derivePickerBindings(
  definitions: ReadonlyArray<TagDefinition>,
): Map<string, PickerBinding> {
  const keysBySource = new Map<string, string>()
  for (const def of definitions) {
    if (def.optionsSource !== undefined) keysBySource.set(def.optionsSource, def.queryKey)
  }
  const bindings = new Map<string, PickerBinding>()
  for (const def of definitions) {
    if (def.optionsSource === undefined) continue
    const parentKey =
      def.optionsSourceParent === undefined
        ? null
        : (keysBySource.get(def.optionsSourceParent) ?? null)
    bindings.set(def.queryKey, {
      source: def.optionsSource,
      parentKey,
      idKey: def.idKey ?? null,
    })
  }
  return bindings
}

/** A row under the twin's own key wins over the implicit emission. */
export function derivePickEmissions(
  tags: ReadonlyArray<TagEntry>,
  bindings: ReadonlyMap<string, PickerBinding>,
): PickEmission[] {
  const claimedKeys = new Set<string>()
  for (const tag of tags) {
    if (tag.key !== '') claimedKeys.add(tag.key)
  }
  const out: PickEmission[] = []
  for (const tag of tags) {
    if (tag.pickedValue === undefined || tag.pickedValue === '') continue
    const binding = bindings.get(tag.key)
    if (!binding || binding.idKey === null) continue
    if (claimedKeys.has(binding.idKey)) continue
    out.push({ nameKey: tag.key, idKey: binding.idKey, idValue: tag.pickedValue })
  }
  return out
}

/** An emitted value is a `connectorId:externalId` reference, so it can name its chain's head. */
export function deriveImpliedKeys(
  tags: ReadonlyArray<TagEntry>,
  emissions: ReadonlyArray<PickEmission>,
  bindings: ReadonlyMap<string, PickerBinding>,
): Set<string> {
  const pickedByKey = new Map<string, string>()
  const valueByKey = new Map<string, string>()
  // A key claimed twice reaches the link in neither form, so it names nothing.
  const claimed = new Set<string>()
  const duplicated = new Set<string>()
  for (const tag of tags) {
    if (tag.key === '') continue
    if (claimed.has(tag.key)) duplicated.add(tag.key)
    claimed.add(tag.key)
    if (tag.value !== '') valueByKey.set(tag.key, tag.value)
    if (tag.pickedValue !== undefined && tag.pickedValue !== '') {
      pickedByKey.set(tag.key, tag.pickedValue)
    }
  }

  const referencesByHead = new Map<string, string[]>()
  const collect = (nameKey: string, reference: string) => {
    const head = chainHead(nameKey, bindings)
    if (head === null) return
    const [key, binding] = head
    if (binding.idKey !== null) return
    const collected = referencesByHead.get(key)
    if (collected === undefined) {
      referencesByHead.set(key, [reference])
    } else {
      collected.push(reference)
    }
  }

  for (const emission of emissions) {
    if (duplicated.has(emission.nameKey)) continue
    collect(emission.nameKey, emission.idValue)
  }
  for (const [nameKey, binding] of bindings) {
    if (binding.idKey === null || duplicated.has(binding.idKey)) continue
    const reference = valueByKey.get(binding.idKey)
    if (reference !== undefined) collect(nameKey, reference)
  }

  const implied = new Set<string>()
  for (const [key, references] of referencesByHead) {
    const picked = pickedByKey.get(key)
    if (picked !== undefined && references.every((x) => connectorOf(x) === picked)) {
      implied.add(key)
    }
  }
  return implied
}

/** Head first, excluding the key itself and anything the payload does not bind. */
export function chainAncestors(
  key: string,
  bindings: ReadonlyMap<string, PickerBinding>,
): string[] {
  return chainPath(key, bindings)
    .slice(0, -1)
    .filter((x) => bindings.has(x))
}

export function chainDescendants(
  key: string,
  bindings: ReadonlyMap<string, PickerBinding>,
): string[] {
  const descendants: string[] = []
  for (const candidate of bindings.keys()) {
    if (candidate !== key && chainAncestors(candidate, bindings).includes(key)) {
      descendants.push(candidate)
    }
  }
  return descendants
}

/** Each chain gathered at its earliest member, head first; everything else left where it is. */
export function orderByChain(
  entries: ReadonlyArray<TagEntry>,
  bindings: ReadonlyMap<string, PickerBinding>,
): TagEntry[] {
  const rankByHead = new Map<string, number>()
  const rankByKey = new Map<string, number>()
  entries.forEach((entry, index) => {
    if (!rankByKey.has(entry.key)) rankByKey.set(entry.key, index)
  })
  const placed = entries.map((entry, index) => {
    const path = chainPath(entry.key, bindings)
    const head = path[0] ?? entry.key
    if (!rankByHead.has(head)) rankByHead.set(head, index)
    return { entry, rank: rankByHead.get(head) ?? index, path, index }
  })
  placed.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank
    // Walk the shared path so a branch keeps its own descendants under it.
    for (let i = 1; i < Math.min(a.path.length, b.path.length); i++) {
      const left = a.path[i]
      const right = b.path[i]
      if (left === undefined || right === undefined || left === right) continue
      return (rankByKey.get(left) ?? entries.length) - (rankByKey.get(right) ?? entries.length)
    }
    return a.path.length - b.path.length || a.index - b.index
  })
  return placed.map((x) => x.entry)
}

function chainPath(key: string, bindings: ReadonlyMap<string, PickerBinding>): string[] {
  const path: string[] = []
  const seen = new Set<string>()
  let current: string | null = key
  while (current !== null && !seen.has(current)) {
    seen.add(current)
    path.unshift(current)
    current = bindings.get(current)?.parentKey ?? null
  }
  return path
}

export function connectorOf(reference: string): string | null {
  const separator = reference.indexOf(':')
  return separator > 0 ? reference.slice(0, separator) : null
}

function chainHead(
  key: string,
  bindings: ReadonlyMap<string, PickerBinding>,
): readonly [key: string, binding: PickerBinding] | null {
  const head = chainPath(key, bindings)[0] ?? key
  const binding = bindings.get(head)
  return binding !== undefined && binding.parentKey === null ? [head, binding] : null
}
